<template>
    <v-expansion-panel
        active-class="activ-accordions"
        class="wrap-accordions-element"
    >
        <v-expansion-panel-header :class="{ 'primary': active }">
            <v-row align="center">
                <v-col cols="12" md="4">
                    <div class="element">
                        <span class="client-name">
                            {{ step.customer.name }}
                        </span>
                        <span>{{ date.toDate() }}</span>
                    </div>
                </v-col>
                <v-col cols="12" md="4">
                    <div class="element">
                        <span class="city"> {{ step.customer.city }}</span>
                    </div>
                </v-col>
                <v-col cols="12" md="4">
                    <div class="element">
                        <v-chip
                            :class="getStateColor(step.step_state)"
                            class="state"
                        >
                            {{ getStateLabel() }}
                        </v-chip>
                        <span class="status-date">{{
                                step.updated_at.toDateTime()
                            }}</span>
                    </div>
                </v-col>
            </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="wrap-detail">
            <v-row>
                <v-col cols="12" md="3">
                    <span class="libelle">
                        {{ $tc("customers.customers", 1) }}
                    </span>
                    <span class="content">
                        {{ step.customer.name }} <br/>
                        <span class="id-client">
                            #{{ String(step.customer.id).padStart(4, 0) }}
                        </span>
                        <br/>
                        <span class="id-client">
                            {{ idLabel }} : {{ step.customer.code }}
                        </span>
                    </span>
                </v-col>
                <v-col cols="12" md="3">
                    <span class="libelle">
                        {{ $tc("customers.address", 1) }}
                    </span>
                    <span class="content">
                        {{ step.customer.address_1 }} <br/>
                        {{ step.customer.address_2 | preventNull }} <br/>
                        {{ step.customer.zipcode }}
                    </span>
                </v-col>
                <v-col cols="12" md="3">
                    <span class="libelle">
                        {{ $t("customers.phone") }}
                    </span>
                    <span class="content">
                        {{ step.customer.phone | preventNull }}
                    </span>
                </v-col>
                <v-col cols="12" md="3">
                    <span class="libelle">
                        {{ $t("customers.recovery_container") }}
                    </span>
                    <span class="content">
                        {{ $t(`global.${step.customer.collect_trays}`) }}
                    </span>
                </v-col>

                <v-col cols="12">
                    <v-data-table
                        :headers="headers"
                        :item-class="getRowBgClass"
                        :items="productsFormatted"
                        :items-per-page="-1"
                        hide-default-footer
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                    >
                        <template #[`item.action`]="{ item }">
                            <v-btn v-if="(step.step_state.code === 'blocking_anomaly' || item.pivot.missing_quantity > 0) && item.pivot.trips_nb !== 999" color="#f64e60" outlined small icon @click="visible = item.id">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </template>

                        <template #[`item.pivot.missing_quantity`]="{ item }">
                            <span :class="item.accentClass">
                                {{ item.pivot.missing_quantity | preventNull }}
                            </span>
                        </template>

                        <template #[`item.pivot.action`]="{ item }">
                            <span>
                                {{ labelAction(item.pivot.action) | preventNull }}
                            </span>
                        </template>
                    </v-data-table>
                </v-col>

                <v-col cols="12" md="3">
                    <span class="libelle">
                        {{ $t("tours.deferred_comment") }}
                    </span>
                    <span class="content">
                        <span v-if="step.delayed_step">
                            {{ step.delayed_step.datetime.toDateTime() }} :
                            {{ step.delayed_step.comment }}
                        </span>
                        <span v-else class="no-data">{{
                                $t("tours.no_comment")
                            }}</span>
                    </span>
                </v-col>
                <v-col cols="12" md="3">
                    <span class="libelle">
                        {{ $t("tours.anomaly_comment") }}
                    </span>
                    <span v-if="step.anomaly">
                        {{ step.anomaly.comment }}
                    </span>
                    <span v-else class="content no-data">{{
                            $t("tours.no_comment")
                        }}</span>
                </v-col>
                <v-col cols="12" md="3">
                    <span class="libelle">
                        {{ $t("tours.anomaly_photo") }}
                    </span>
                    <span v-if="step.anomaly && step.anomaly.picture_path">
                        <img
                            :src="step.anomaly.picture_path"
                            class="anomaly-picture"
                            @click="openNewTab(step.anomaly.picture_path)"
                        />
                    </span>
                    <span v-else class="content no-data">
                        {{ $t("tours.no_photo") }}
                    </span>
                </v-col>
                <v-col cols="12" md="3">
                    <span class="libelle">
                        {{ recoveredContainer }}
                    </span>
                    <span class="content">
                        <span v-if="step.customer.collect_trays && stepIsDone">
                            {{ $t(`global.${step.trays_picked_up}`) }}
                        </span>
                        <span v-else> - </span>
                    </span>
                </v-col>
                <v-col cols="12" md="3">
                    <span class="libelle">
                        {{ $t("tours.photo") }}
                    </span>
                    <span v-if="step.step_pictures && step.step_pictures.length >= 1" style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap">
                        <img
                            v-for="picture in step.step_pictures"
                            :key="picture"
                            :src="picture"
                            class="anomaly-picture"
                            @click="openNewTab(picture)"
                        />
                    </span>
                    <span v-else class="content no-data">
                        {{ $t("tours.no_photo") }}
                    </span>
                </v-col>
                <template v-if="step.step_state.code !== 'in_progress'">
                    <v-col cols="12">
                        <v-divider></v-divider>
                    </v-col>
                    <v-col cols="12" md="3">
                        <span class="libelle">
                            {{ $t("tours.signature") }}
                        </span>
                        <span v-if="step.signature_url !== null" class="content">
                            <img
                                :src="step.signature_url"
                                class="anomaly-picture"
                                @click="openNewTab(step.signature_url)"
                            />
                        </span>
                        <span v-else class="content no-data">
                            {{ $t("tours.no_signature") }}
                        </span>
                    </v-col>
                    <v-col cols="12" md="3">
                        <span class="libelle">
                            {{ $t("tours.signature_comment") }}
                        </span>
                        <span class="content">
                        {{ step.signature_comment | preventNull }}
                    </span>
                    </v-col>
                </template>
            </v-row>

            <confirm-lock :visible="visible"
                          message="Voulez-vous vraiment abandonner ce colis ?"
                          @close="visible = false"
                          @validate="cancelProduct"
            ></confirm-lock>
        </v-expansion-panel-content>
    </v-expansion-panel>
</template>

<script>
import step_states from "../../mixins/step_states";
import {
    STEP_BLOCKING_ANOMALY_STATE,
    STEP_IN_PROGRESS_STATE,
    TOUR_DELIVERY_ACTION,
    TOUR_COLLECT_ACTION
} from "../../constants";
import Config from "../../config.singleton";
import ConfirmLock from "../dialogs/confirmLock.vue";
import { showSnackbar } from "../../functions";

export default {
    name: "DeliveryPoint",
    components: { ConfirmLock },
    mixins: [step_states],
    props: {
        step: {
            required: true,
            type: Object,
        },
        date: {
            required: true,
            type: String,
        },
        active: {
            type: Boolean,
            required: true,
        }
    },
    data() {
        return {
            visible: false,
            headers: [
                {
                    text: this.$t("tours.designation"),
                    align: "start",
                    sortable: true,
                    value: "label",
                },
                {
                    text: this.$t("tours.wording_packaging"),
                    align: "start",
                    sortable: true,
                    value: "packaging",
                },
                {
                    text: this.$t("tours.action"),
                    align: "start",
                    sortable: true,
                    value: "pivot.action",
                },
                {
                    text: this.$t("tours.expected_quantity"),
                    align: "center",
                    sortable: true,
                    value: "quantity",
                },
                {
                    text: this.$t("tours.delivered_quantity"),
                    align: "center",
                    sortable: true,
                    value: "pivot.delivered_quantity",
                },
                {
                    text: this.$t("tours.missing_quantity"),
                    align: "center",
                    sortable: true,
                    value: "pivot.missing_quantity",
                },
                {
                    value: "action"
                },
            ],
            sortBy: ['pivot.type', 'label'],
            sortDesc: [false, false],
        };
    },

    computed: {
        recoveredContainer() {
            return Config.recovered_container ?? this.$t("tours.recovered_container")
        },
        productsFormatted() {
            return this.step.products.map((product) => {
                product.bgClass = "";
                product.accentClass = "";
                if (this.step.step_state.code === STEP_BLOCKING_ANOMALY_STATE) {
                    product.bgClass = "bg-blocking-anomaly";
                    product.pivot.missing_quantity = null;
                }
                if (product.pivot.missing_quantity > 0) {
                    product.bgClass = "bg-non-blocking-anomaly";
                    product.accentClass = "missing";
                }
                return product;
            });
        },
        stepIsDone() {
            return this.step.step_state.code !== STEP_IN_PROGRESS_STATE;
        },
        idLabel() {
            return Config.id_label ?? this.$t("customers.code")
        },
    },

    methods: {
        getRowBgClass(item) {
            return item.bgClass;
        },
        openNewTab(path) {
            window.open(path, "_blank");
        },
        labelAction(item) {
            switch (item) {
                case TOUR_DELIVERY_ACTION:
                    return this.$t("tours.delivery");
                case TOUR_COLLECT_ACTION:
                    return this.$t("tours.collect");
                default:
                    return null;
            }
        },
        getStateLabel() {
            let label = this.$t(`step_states.${this.step.step_state.code}`)

            if (this.step.anomaly?.anomaly?.label === 'payement_failure') {
                label += ` : ${this.step.anomaly.anomaly.designation}`
            }

            return label
        },
        cancelProduct() {
            const itemId = this.visible;

            this.$api.tours.cancelPackage(this.step.tour_id, {
                product_id: itemId,
                tour_id: this.step.tour_id,
                step_id: this.step.id,
            }).then(() => {
                showSnackbar('success', "Nettoyage effectué avec succès")
            });
        },
    },
};
</script>

<style scoped></style>
